import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';

function SchoolSidebar({ schoolData, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Get user role data if logged in as user
  const [userRoleData, setUserRoleData] = useState(null);
  
  useEffect(() => {
    const userData = localStorage.getItem('userRoleData');
    if (userData) {
      try {
        setUserRoleData(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user role data:', e);
      }
    }
  }, []);
  
  // Check if user has access to a page
  const hasAccess = (pageId) => {
    // If no user role data, assume full access (school admin login)
    if (!userRoleData) return true;
    
    // Check if page is in user's permissions
    const permissions = userRoleData.page_permissions || [];
    return permissions.includes(pageId);
  };
  
  // Define section paths for auto-expansion
  const sectionPaths = {
    directory: ['/school/students', '/school/parents', '/school/teachers', '/school/receptionists', '/school/transport-persons', '/school/users'],
    admissions: ['/school/student-approvals'],
    academics: ['/school/classes', '/school/curriculum-form', '/school/class-schedule', '/school/schedule-view', '/school/homework', '/school/exam-schedule', '/school/exam-report-cards', '/school/report-cards'],
    attendance: ['/school/attendance-records'],
    fees: ['/school/annual-fees-master', '/school/class-mapping', '/school/billing', '/school/invoice-preview', '/school/payments-receipts', '/school/transactions-preview', '/school/transactions-reports', '/school/receipts-report', '/school/statements-preview', '/school/statement-header-footer'],
    communication: ['/school/notices', '/school/chat', '/school/chat-groups', '/school/admin-chats'],
    transport: ['/school/transport-persons', '/school/vehicle', '/school/routes', '/school/pickup-locations', '/school/bus-seat-management'],
    meals: ['/school/meals'],
    reports: ['/school/bi-reports', '/school/bi-reports/dashboard', '/school/bi-reports/reports'],
    settings: ['/school/settings', '/school/timing', '/school/schedule', '/school/curriculum-form', '/school/annual-calendar-form', '/school/policies', '/school/contact-us', '/school/upgrade']
  };

  // Auto-expand sections based on current path
  const getInitialExpandedState = () => {
    const currentPath = location.pathname;
    const expanded = {};
    Object.keys(sectionPaths).forEach(section => {
      expanded[section] = sectionPaths[section].some(path => currentPath === path);
    });
    return expanded;
  };

  // State for managing expanded sections
  const [expandedSections, setExpandedSections] = useState(getInitialExpandedState());

  // Update expanded state when location changes
  useEffect(() => {
    setExpandedSections(getInitialExpandedState());
  }, [location.pathname]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleLogout = async () => {
    try {
      // Check if user is logged in via user roles
      const isUserRoleAuth = localStorage.getItem('userRoleAuthenticated');
      
      if (isUserRoleAuth) {
        // Clear user role authentication
        localStorage.removeItem('userRoleAuthenticated');
        localStorage.removeItem('userRoleData');
        navigate('/userlogin');
        return;
      }
      
      // Sign out from Supabase (school admin)
      await supabase.auth.signOut();
      
      // Clear localStorage
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      
      // Call the parent logout handler if provided
      if (onLogout) {
        onLogout();
      } else {
        // Redirect to school login page
        navigate('/schoollogin');
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear localStorage and redirect even if there's an error
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      localStorage.removeItem('userRoleAuthenticated');
      localStorage.removeItem('userRoleData');
      navigate('/schoollogin');
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isSectionActive = (paths) => {
    return paths.some(path => isActive(path));
  };

  const NavItem = ({ path, label, onClick }) => (
    <button
      className={`nav-item ${isActive(path) ? 'active' : ''}`}
      onClick={onClick || (() => navigate(path))}
    >
      <span className="nav-label">{label}</span>
    </button>
  );

  const SectionHeader = ({ section, label }) => {
    const paths = sectionPaths[section] || [];
    return (
      <button
        className={`nav-section-header ${isSectionActive(paths) ? 'active' : ''}`}
        onClick={() => toggleSection(section)}
      >
        <span className="nav-label">{label}</span>
        <span className="nav-arrow">{expandedSections[section] ? '▼' : '▶'}</span>
      </button>
    );
  };

  const SubNavItem = ({ path, label }) => (
    <button
      className={`nav-sub-item ${isActive(path) ? 'active' : ''}`}
      onClick={() => navigate(path)}
    >
      <span className="nav-label">{label}</span>
    </button>
  );

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <img 
          src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
          alt="Nexy App Logo" 
          className="sidebar-logo"
        />
        {schoolData?.id && (
          <div className="sidebar-branch-selector">
            <BranchSelector 
              schoolId={schoolData.id} 
              selectedBranchId={selectedBranchId}
              onBranchChange={handleBranchChange}
            />
          </div>
        )}
      </div>
      
      <div style={{ height: '1rem', flexShrink: 0 }}></div>
      
      <nav className="sidebar-nav">
        {/* 1. Dashboard */}
        {hasAccess('dashboard') && (
          <NavItem path="/school/dashboard" label="Dashboard" />
        )}

        {/* 2. Directory */}
        {(hasAccess('students') || hasAccess('parents') || hasAccess('teachers') || hasAccess('receptionists') || hasAccess('transport-persons') || hasAccess('users')) && (
          <div className="nav-section">
            <SectionHeader 
              section="directory" 
              label="Directory"
            />
            {expandedSections.directory && (
              <div className="nav-sub-items">
                {hasAccess('students') && <SubNavItem path="/school/students" label="Students" />}
                {hasAccess('parents') && <SubNavItem path="/school/parents" label="Parents" />}
                {hasAccess('teachers') && <SubNavItem path="/school/teachers" label="Teachers" />}
                {hasAccess('receptionists') && <SubNavItem path="/school/receptionists" label="Receptionists" />}
                {hasAccess('transport-persons') && <SubNavItem path="/school/transport-persons" label="Drivers" />}
                {hasAccess('users') && <SubNavItem path="/school/users" label="User" />}
              </div>
            )}
          </div>
        )}

        {/* 3. Admissions */}
        {hasAccess('student-approvals') && (
          <div className="nav-section">
            <SectionHeader 
              section="admissions" 
              label="Admissions"
            />
            {expandedSections.admissions && (
              <div className="nav-sub-items">
                <SubNavItem path="/school/student-approvals" label="Review - Approve / Reject" />
              </div>
            )}
          </div>
        )}

        {/* 4. Academics */}
        {(hasAccess('classes') || hasAccess('curriculum-form') || hasAccess('class-schedule') || hasAccess('schedule-view') || hasAccess('homework') || hasAccess('exam-schedule') || hasAccess('exam-report-cards') || hasAccess('report-cards')) && (
          <div className="nav-section">
            <SectionHeader 
              section="academics" 
              label="Academics"
            />
            {expandedSections.academics && (
              <div className="nav-sub-items">
                {hasAccess('curriculum-form') && <SubNavItem path="/school/curriculum-form" label="Curriculum" />}
                {hasAccess('classes') && <SubNavItem path="/school/classes" label="Classes" />}
                {hasAccess('class-schedule') && <SubNavItem path="/school/class-schedule" label="Class Schedule" />}
                {hasAccess('schedule-view') && <SubNavItem path="/school/schedule-view" label="Schedule View" />}
                {hasAccess('homework') && <SubNavItem path="/school/homework" label="Homework" />}
                {hasAccess('exam-schedule') && <SubNavItem path="/school/exam-schedule" label="Exams" />}
                {hasAccess('exam-report-cards') && <SubNavItem path="/school/exam-report-cards" label="Report Cards" />}
              </div>
            )}
          </div>
        )}

        {/* 5. Attendance */}
        {hasAccess('attendance-records') && (
          <div className="nav-section">
            <SectionHeader 
              section="attendance" 
              label="Attendance"
            />
            {expandedSections.attendance && (
              <div className="nav-sub-items">
                <SubNavItem path="/school/attendance-records" label="View Attendance - Daily Tracking - Reports" />
              </div>
            )}
          </div>
        )}

        {/* 6. Fees & Billing */}
        {hasAccess('set-fees') && (
          <div className="nav-section">
            <SectionHeader 
              section="fees" 
              label="Fees & Billing"
            />
            {expandedSections.fees && (
              <div className="nav-sub-items">
                <SubNavItem path="/school/annual-fees-master" label="Annual Fees Master" />
                <SubNavItem path="/school/class-mapping" label="Class Fee Mapping" />
                <SubNavItem path="/school/billing" label="Billing" />
                <SubNavItem path="/school/invoice-preview" label="Invoice Preview & Print" />
                <SubNavItem path="/school/payments-receipts" label="Payments & Receipts" />
                <SubNavItem path="/school/transactions-reports" label="Transactions Reports" />
                <SubNavItem path="/school/receipts-report" label="Receipts Report" />
                <SubNavItem path="/school/statements-preview" label="Statements Preview" />
                <SubNavItem path="/school/statement-header-footer" label="Statement Header & Footer" />
              </div>
            )}
          </div>
        )}

        {/* 7. Communication */}
        {(hasAccess('notices') || hasAccess('chat') || hasAccess('chat-groups') || hasAccess('admin-chats')) && (
          <div className="nav-section">
            <SectionHeader 
              section="communication" 
              label="Communication"
            />
            {expandedSections.communication && (
              <div className="nav-sub-items">
                {hasAccess('notices') && <SubNavItem path="/school/notices" label="Notices" />}
                {hasAccess('chat') && <SubNavItem path="/school/chat" label="Chat" />}
                {hasAccess('chat-groups') && <SubNavItem path="/school/chat-groups" label="Chat Groups" />}
                {hasAccess('admin-chats') && <SubNavItem path="/school/admin-chats" label="Admin Chats" />}
              </div>
            )}
          </div>
        )}

        {/* 8. Transport */}
        {(hasAccess('transport-persons') || hasAccess('vehicle') || hasAccess('routes') || hasAccess('pickup-locations') || hasAccess('bus-seat-management')) && (
          <div className="nav-section">
            <SectionHeader 
              section="transport" 
              label="Transport"
            />
            {expandedSections.transport && (
              <div className="nav-sub-items">
                {hasAccess('transport-persons') && <SubNavItem path="/school/transport-persons" label="Drivers" />}
                {hasAccess('vehicle') && <SubNavItem path="/school/vehicle" label="Vehicles" />}
                {hasAccess('routes') && <SubNavItem path="/school/routes" label="Routes" />}
                {hasAccess('pickup-locations') && <SubNavItem path="/school/pickup-locations" label="Pickup Locations" />}
                {hasAccess('bus-seat-management') && <SubNavItem path="/school/bus-seat-management" label="Seat Management" />}
              </div>
            )}
          </div>
        )}

        {/* 9. Meal Management */}
        {hasAccess('meals') && (
          <div className="nav-section">
            <SectionHeader 
              section="meals" 
              label="Meal Management"
            />
            {expandedSections.meals && (
              <div className="nav-sub-items">
                <SubNavItem path="/school/meals" label="Menus" />
              </div>
            )}
          </div>
        )}

        {/* 10. BI & Reports */}
        {(hasAccess('bi-reports') || hasAccess('bi-reports/dashboard') || hasAccess('bi-reports/reports')) && (
          <div className="nav-section">
            <SectionHeader 
              section="reports" 
              label="BI & Reports"
            />
            {expandedSections.reports && (
              <div className="nav-sub-items">
                {hasAccess('bi-reports') && <SubNavItem path="/school/bi-reports/dashboard" label="Dashboard" />}
                {hasAccess('bi-reports') && <SubNavItem path="/school/bi-reports/reports" label="Reports" />}
              </div>
            )}
          </div>
        )}

        {/* 11. School Settings */}
        {(hasAccess('settings') || hasAccess('timing') || hasAccess('schedule') || hasAccess('annual-calendar-form') || hasAccess('curriculum-form') || hasAccess('policies') || hasAccess('contact-us') || hasAccess('upgrade')) && (
          <div className="nav-section">
            <SectionHeader 
              section="settings" 
              label="School Settings"
            />
            {expandedSections.settings && (
              <div className="nav-sub-items">
                {hasAccess('settings') && <SubNavItem path="/school/settings" label="Settings" />}
                {hasAccess('timing') && <SubNavItem path="/school/timing" label="School Timings" />}
                {hasAccess('schedule') && <SubNavItem path="/school/schedule" label="Holiday Selection" />}
                {hasAccess('annual-calendar-form') && <SubNavItem path="/school/annual-calendar-form" label="Academic Year" />}
                {hasAccess('curriculum-form') && <SubNavItem path="/school/curriculum-form" label="Curriculum" />}
                {hasAccess('policies') && <SubNavItem path="/school/policies" label="Policies / Handbook" />}
                {hasAccess('contact-us') && <SubNavItem path="/school/contact-us" label="Contact Us" />}
                {hasAccess('upgrade') && <SubNavItem path="/school/upgrade" label="Upgrade" />}
              </div>
            )}
          </div>
        )}

        {/* 12. My Account */}
        <NavItem path="/school/my-account" label="My Account" />
      </nav>
      
      <div className="sidebar-footer">
        <button className="logout-btn" onClick={handleLogout}>
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}

export default SchoolSidebar;
